import React, { useState } from 'react';
import { AlertOctagon, BellRing, Loader2, Truck, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { suppliersAPI } from '../services/api';
import RescheduleModal from './RescheduleModal';

const fmt = (d) => new Date(d).toLocaleDateString('fr-FR');

// Dashboard: open purchase orders past their expected delivery date, with one-click supplier reminder
const LatePurchaseOrdersAlert = ({ items, onReminded, onOpenSuppliers }) => {
  const [busy, setBusy] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  if (!items?.length) return null;

  const remind = async (po) => {
    setBusy(po.id);
    try {
      const r = await suppliersAPI.remindPurchaseOrder(po.id);
      toast.success(r.message);
      onReminded && onReminded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relance impossible');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border-l-4 border-red-600 mb-8" data-testid="late-po-alert">
      <div className="p-5 flex items-start gap-3">
        <AlertOctagon size={22} className="text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h2 className="font-semibold text-gray-900" data-testid="late-po-title">
            {items.length} bon{items.length > 1 ? 's' : ''} de commande en retard de livraison
          </h2>
          <p className="text-sm text-gray-500">La date de livraison prévue est dépassée et la marchandise n'est pas (entièrement) réceptionnée. Relancez le fournisseur ou réceptionnez depuis l'onglet Fournisseurs.</p>
        </div>
      </div>
      <div className="px-5 pb-5 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="late-po-table">
          <thead className="bg-red-50 text-xs text-red-800 uppercase">
            <tr>
              <th className="px-3 py-2 text-left">N° bon</th>
              <th className="px-3 py-2 text-left">Fournisseur</th>
              <th className="px-3 py-2 text-left">Prévu le</th>
              <th className="px-3 py-2 text-right">Retard</th>
              <th className="px-3 py-2 text-right">Reste attendu</th>
              <th className="px-3 py-2 text-left">Relances</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map(po => (
              <tr key={po.id} data-testid={`late-po-row-${po.id}`}>
                <td className="px-3 py-2 font-medium text-gray-900">{po.number}<span className="block text-[11px] font-normal text-gray-400">{po.status}</span></td>
                <td className="px-3 py-2"><button type="button" onClick={onOpenSuppliers} className="flex items-center gap-1 text-[#1e3a5f] hover:underline" data-testid={`late-po-supplier-${po.id}`}><Truck size={13} /> {po.supplier_name}</button></td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmt(po.expected_date)}</td>
                <td className="px-3 py-2 text-right"><span className="inline-block bg-red-100 text-red-700 font-semibold rounded-full px-2 py-0.5 text-xs" data-testid={`late-po-days-${po.id}`}>+{po.days_late} j</span></td>
                <td className="px-3 py-2 text-right font-semibold text-amber-700">{po.remaining_units} u. <span className="text-xs font-normal text-gray-400">({po.lines_pending} ligne{po.lines_pending > 1 ? 's' : ''})</span></td>
                <td className="px-3 py-2 text-xs text-gray-500" data-testid={`late-po-reminders-${po.id}`}>{po.reminders_count ? `${po.reminders_count} · dernière le ${fmt(po.last_reminder_at)}` : 'Aucune'}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                  <button type="button" data-testid={`late-po-reschedule-${po.id}`} onClick={() => setRescheduling(po)} title="Enregistrer une nouvelle date promise par le fournisseur"
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#1e3a5f] border border-[#1e3a5f]/30 hover:bg-blue-50 rounded px-2.5 py-1.5">
                    <CalendarClock size={13} /> Nouvelle date{po.reschedules_count ? ` (${po.reschedules_count})` : ''}
                  </button>
                  <button type="button" data-testid={`late-po-remind-${po.id}`} onClick={() => remind(po)} disabled={busy === po.id || !po.supplier_email}
                    title={po.supplier_email ? `Envoyer une relance à ${po.supplier_email}` : 'Aucun e-mail renseigné pour ce fournisseur'}
                    className="inline-flex items-center gap-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded px-2.5 py-1.5 disabled:opacity-50">
                    {busy === po.id ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />} Relancer
                  </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rescheduling && <RescheduleModal po={rescheduling} onClose={() => setRescheduling(null)} onSaved={() => { setRescheduling(null); onReminded && onReminded(); }} />}
    </div>
  );
};

export default LatePurchaseOrdersAlert;
